import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
    DisruptionMessage,
    DisruptionMessageEntity,
    DisruptionMessageRequest,
    DisruptionMessageResponse,
    DisruptionMessagesSummary,
    DisruptionMessageType
} from '../ridango.models';
import { map } from 'rxjs/operators';
import Env from '../../env';
import { AlertsService } from './alerts.service';
import { Temporal } from '@js-temporal/polyfill';
import { MessageTypeService } from './message-type.service';

@Injectable({
    providedIn: 'root'
})
export class DisruptionMessagesService {
    constructor(
        @Inject('ENV') private env: Env,
        private http: HttpClient,
        private alertsService: AlertsService,
        private messageTypeService: MessageTypeService
    ) {}

    getMessages(params?: DisruptionMessageRequest): Observable<DisruptionMessageResponse> {
        if (this.env.WMB?.DISRUPTION_MESSAGES) {
            return this.alertsService.getAlerts(params);
        }
        return this.http
            .get<DisruptionMessageResponse>(
                `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/disruption-message/W`
            )
            .pipe(
                map((response) => {
                    response.messages = response?.messages?.map((message) => {
                        return { ...message, messageType: this.messageTypeService.determineMessageType(message) };
                    });

                    return response;
                })
            );
    }

    getCurrentSeconds(): number {
        return Temporal.Now.instant().toZonedDateTimeISO(this.env.TIMEZONE).epochSeconds;
    }

    getSummary(messages: DisruptionMessage[]): DisruptionMessagesSummary[] {
        const summary: DisruptionMessagesSummary[] = [];

        const general = new DisruptionMessagesSummary(
            0,
            'warning-outline',
            DisruptionMessageType.ALERT_GENERIC,
            '/img/disruption-messages/generic.svg'
        );
        const bus = new DisruptionMessagesSummary(
            0,
            'bus-outline',
            DisruptionMessageType.ALERT_BUS,
            '/img/disruption-messages/bus.svg'
        );
        const stop = new DisruptionMessagesSummary(
            0,
            'bus-outline',
            DisruptionMessageType.ALERT_STOP,
            '/img/disruption-messages/stop.svg'
        );
        messages?.forEach((message: DisruptionMessage) => {
            if (this.env.APP_NAME === 'sormland') {
                if (this.messageTypeService.isBusDisruption(message)) {
                    bus.amount += message.informedEntities.length;
                } else if (this.messageTypeService.isStopDisruption(message)) {
                    const countStopsInMessage = message.informedEntities
                        .map((entity) => entity.stopId)
                        .filter((value, index, self) => self.indexOf(value) === index).length;
                    stop.amount += countStopsInMessage;
                }
            } else if (this.env.WMB?.DISRUPTION_MESSAGES) {
                if (this.messageTypeService.isBusDisruption(message)) {
                    bus.amount++;
                }
                if (
                    this.messageTypeService.isStopDisruption(message) ||
                    this.messageTypeService.isBusAndStopDisruption(message)
                ) {
                    stop.amount++;
                }
                if (
                    !this.messageTypeService.isBusDisruption(message) &&
                    !this.messageTypeService.isStopDisruption(message) &&
                    !this.messageTypeService.isBusAndStopDisruption(message)
                ) {
                    general.amount++;
                }
            } else {
                if (this.messageTypeService.isGenericDisruption(message)) {
                    general.amount++;
                }
                message.informedEntities.forEach((entity: DisruptionMessageEntity) => {
                    if (entity.routeType === 'B') {
                        bus.amount++;
                    }
                    if (entity.stopId !== null) {
                        stop.amount++;
                    }
                });
            }
        });

        if (!this.env.DISRUPTION_MESSAGES?.MESSAGES_TYPES) {
            summary.push(general, stop, bus);
        }

        if (this.env.DISRUPTION_MESSAGES?.MESSAGES_TYPES?.includes('GENERIC')) {
            summary.push(general);
        }

        if (this.env.DISRUPTION_MESSAGES?.MESSAGES_TYPES?.includes('STOP')) {
            summary.push(stop);
        }

        if (this.env.DISRUPTION_MESSAGES?.MESSAGES_TYPES?.includes('BUS')) {
            summary.push(bus);
        }
        return summary;
    }

    getCategories(messages: DisruptionMessage[]): string[] {
        const categories: string[] = [];
        messages?.forEach((message: DisruptionMessage) => {
            if (this.messageTypeService.isBusDisruption(message) && categories.indexOf('ALERT_BUS') === -1) {
                categories.push('ALERT_BUS');
            }
            if (this.messageTypeService.isStopDisruption(message) && categories.indexOf('ALERT_STOP') === -1) {
                categories.push('ALERT_STOP');
            }
        });
        return categories;
    }

    getDirection(messages: DisruptionMessage[]): string[] {
        return [
            ...new Set(
                messages.flatMap((obj) =>
                    obj.informedEntities
                        .filter((entity) => entity.tripHeadsign?.trim())
                        .map((entity) => entity.tripHeadsign?.trim())
                )
            )
        ];
    }

    getRoutes(messages: DisruptionMessage[]): { routeShortName: string; routeId: number | null }[] {
        const routes: { routeShortName: string; routeId: number | null }[] = [];
        messages?.forEach((message: DisruptionMessage) => {
            message.informedEntities.forEach((entity: DisruptionMessageEntity) => {
                const route = { routeShortName: entity.routeShortName, routeId: entity?.routeId || null };
                if (entity.routeShortName && !routes.some((r) => r.routeShortName === route.routeShortName)) {
                    routes.push(route);
                }
            });
        });
        return routes;
    }
}
